import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, Diamond, BookOpen, Users, Calendar, PartyPopper } from 'lucide-react';
import confetti from 'canvas-confetti';

const API_URL = window.location.origin;

// Suggested action icons
const ACTION_ICONS = {
  journal: BookOpen,
  community: Users,
  coaching: Calendar,
  celebrate: PartyPopper
};

const MilestoneNotification = () => {
  const navigate = useNavigate();
  const [milestone, setMilestone] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);

  useEffect(() => {
    checkForMilestone();
  }, []);

  const checkForMilestone = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_URL}/api/user/journey/milestone-check`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.has_milestone) {
        setMilestone(response.data);
        // Delay showing the popup slightly
        setTimeout(() => setIsOpen(true), 1000);
      }
    } catch (error) {
      console.error('Failed to check milestone:', error);
    }
  };

  const handleAcknowledge = async () => {
    if (!milestone) return;

    setAcknowledging(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/user/journey/acknowledge-milestone/${milestone.day}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Trigger confetti for celebration
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#C97B5D', '#1A3C34', '#F5E6D3', '#D4A574']
      });

      setIsOpen(false);
    } catch (error) {
      console.error('Failed to acknowledge milestone:', error);
    } finally {
      setAcknowledging(false);
    }
  };

  const handleAction = (action) => {
    handleAcknowledge();
    
    // Navigate based on suggested action
    switch (action) {
      case 'journal':
        navigate('/journal');
        break;
      case 'community':
        navigate('/communities');
        break;
      case 'coaching':
        navigate('/sessions');
        break;
      default:
        // Just close for celebrate
        break;
    }
  };

  if (!milestone || !isOpen) return null;

  const ActionIcon = ACTION_ICONS[milestone.milestone?.suggested_action] || Sparkles;
  const dayLabel = milestone.day === 90 ? 'Journey Complete!' : `Day ${milestone.day}`;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden">
        {/* Decorative Header */}
        <div className="bg-gradient-to-br from-terracotta to-terracotta/80 p-8 text-white text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-4">
            <Sparkles className="h-8 w-8" />
          </div>
          <p className="text-white/90 text-sm font-medium mb-1 tracking-wide">90-Day Journey</p>
          <h2 className="font-heading text-2xl font-bold text-white">{dayLabel}</h2>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          <h3 className="font-heading text-xl font-semibold text-forest mb-2">
            {milestone.milestone?.title}
          </h3>
          <p className="text-gray-600 mb-4">
            {milestone.milestone?.message}
          </p>

          {/* Focus-specific encouragement */}
          {milestone.focus_encouragement && (
            <div className="p-4 bg-sand/50 rounded-xl mb-4 text-left">
              <p className="text-sm text-gray-500 mb-1">Reflection for your {milestone.focus_area} journey:</p>
              <p className="text-forest italic">&ldquo;{milestone.focus_encouragement}&rdquo;</p>
            </div>
          )}

          {/* Diamond Reward */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-full text-amber-700 mb-6">
            <Diamond className="h-5 w-5" />
            <span className="font-semibold">+{milestone.milestone?.diamond_reward} Diamonds</span>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            {milestone.milestone?.suggested_action && milestone.milestone.suggested_action !== 'celebrate' && (
              <Button
                onClick={() => handleAction(milestone.milestone.suggested_action)}
                className="w-full bg-terracotta hover:bg-terracotta/90 text-white"
              >
                <ActionIcon className="h-4 w-4 mr-2" />
                {milestone.milestone.suggested_action === 'journal' && 'Reflect in Journal'}
                {milestone.milestone.suggested_action === 'community' && 'Connect with Community'}
                {milestone.milestone.suggested_action === 'coaching' && 'Book a Session'}
              </Button>
            )}
            <Button
              onClick={handleAcknowledge}
              variant={milestone.milestone?.suggested_action === 'celebrate' ? 'default' : 'outline'}
              className={milestone.milestone?.suggested_action === 'celebrate' 
                ? 'w-full bg-terracotta hover:bg-terracotta/90 text-white' 
                : 'w-full'}
              disabled={acknowledging}
            >
              {acknowledging ? 'Claiming...' : (
                milestone.milestone?.suggested_action === 'celebrate' 
                  ? '🎉 Celebrate & Claim Diamonds' 
                  : 'Claim Diamonds'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MilestoneNotification;
